import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Theme } from '@fluentui/react-components';
import { useTeams } from '@/lib/teams/teams-context';
import {
  applyDocumentTheme,
  detectBrowserTheme,
  documentThemeFor,
  fluentThemeFor,
  readStoredTheme,
  storeTheme,
  type DocumentThemeName,
  type TeamsThemeName,
} from '@/lib/teams/theme';

/**
 * Single source of truth for the visual theme.
 *
 * Inside Teams the host always wins (the user changes it in Teams settings).
 * In the browser we honour a stored preference, falling back to the OS.
 */

export interface ThemeContextValue {
  theme: TeamsThemeName;
  documentTheme: DocumentThemeName;
  fluentTheme: Theme;
  /** `teams` when the host dictates the theme and the user cannot override it. */
  source: 'teams' | 'user' | 'system';
  canChangeTheme: boolean;
  setTheme: (theme: TeamsThemeName) => void;
  toggleDarkMode: () => void;
}

/**
 * Light theme fallback. Having a real default (instead of `null`) lets design
 * system components be unit tested in isolation without mounting the provider.
 */
const DEFAULT_THEME_CONTEXT: ThemeContextValue = {
  theme: 'default',
  documentTheme: 'light',
  fluentTheme: fluentThemeFor('default'),
  source: 'system',
  canChangeTheme: false,
  setTheme: () => undefined,
  toggleDarkMode: () => undefined,
};

const ThemeContext = createContext<ThemeContextValue>(DEFAULT_THEME_CONTEXT);

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const teams = useTeams();
  const [override, setOverride] = useState<TeamsThemeName | null>(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = useState<TeamsThemeName>(() => detectBrowserTheme());

  // Follow the OS while the user has not chosen explicitly (browser only).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const dark = window.matchMedia('(prefers-color-scheme: dark)');
    const contrast = window.matchMedia('(forced-colors: active)');
    const update = (): void => setSystemTheme(detectBrowserTheme());

    dark.addEventListener('change', update);
    contrast.addEventListener('change', update);
    return () => {
      dark.removeEventListener('change', update);
      contrast.removeEventListener('change', update);
    };
  }, []);

  const source: ThemeContextValue['source'] = teams.isInTeams
    ? 'teams'
    : override
      ? 'user'
      : 'system';

  const theme: TeamsThemeName = teams.isInTeams ? teams.theme : (override ?? systemTheme);

  useEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: TeamsThemeName) => {
    setOverride(next);
    storeTheme(next);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setOverride((previous) => {
      const current = previous ?? detectBrowserTheme();
      const next: TeamsThemeName = current === 'dark' ? 'default' : 'dark';
      storeTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      documentTheme: documentThemeFor(theme),
      fluentTheme: fluentThemeFor(theme),
      source,
      canChangeTheme: !teams.isInTeams,
      setTheme,
      toggleDarkMode,
    }),
    [theme, source, teams.isInTeams, setTheme, toggleDarkMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeContextValue {
  return useContext(ThemeContext);
}
